/**
 * Draining the outbox to the platform.
 *
 * Specification: ../../../../apps/pos/test/api-client.spec.ts.
 * Master plan §3 K4, §2 D1, §2 D6.
 *
 * HERE RATHER THAN IN THE POS, because the kitchen screen drains an outbox too
 * and the second copy would have been the third time this codebase learned that
 * lesson. Every device that emits events needs exactly this transport, and the
 * rules below are the ones a second author would relax.
 *
 * The outbox already owns the hard parts — blocking FIFO, quarantine after a
 * bounded number of rejections, and not spending an event's attempt budget on a
 * transport failure. This is only the transport, and it has two jobs it must
 * not get wrong.
 *
 * IT MUST NOT USE `JSON.stringify`. The payloads carry money as bigint, and
 * `JSON.stringify` throws on one outright — correctly, which is exactly why the
 * tagged codec exists. This codebase has already been bitten once: the sqlite
 * store's unit test used money as a decimal string and passed, while the POS
 * emits bigint. Only an end-to-end test found it.
 *
 * IT MUST THROW ON AN HTTP FAILURE rather than returning a rejection. That is
 * the signal `Outbox.drain()` uses to tell "the server refused this event" from
 * "the server was not there" — and only the second spares the attempt budget.
 * Returning a rejection for a 502 would quarantine a whole shift's takings
 * during one server restart.
 */

import { encodePayload } from "@gf/kernel/json";
import type { OutboundEvent, UploadResult, Uploader } from "./outbox";

export interface UploaderArgs {
  readonly baseUrl: string;
  readonly apiKey: string;
  readonly venueId: string;
  readonly deviceId: string;
  readonly fetch?: typeof fetch;
}

/**
 * A key derived from the batch, never random.
 *
 * A random key makes a retry a different request, which defeats the mechanism:
 * the server replays the original response for a repeated key, and that is what
 * stops a slow accept — one whose response never arrived — from being counted
 * as a failure and sent again.
 *
 * The event ids already deduplicate the WRITE; this deduplicates the REQUEST.
 */
function idempotencyKey(batch: readonly OutboundEvent[], deviceId: string): string {
  const ids = batch.map((e) => e.eventId).join(",");
  let hash = 0x811c9dc5;
  for (const char of `${deviceId}:${ids}`) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  // Shaped as a UUID because the platform's idempotency middleware expects one.
  const hex = hash.toString(16).padStart(8, "0");
  return `${hex}-0000-4000-8000-${deviceId.replace(/-/g, "").slice(0, 12)}`;
}

export function createUploader(args: UploaderArgs): Uploader {
  const send = args.fetch ?? fetch;

  return async (batch: readonly OutboundEvent[]): Promise<UploadResult> => {
    const body = encodePayload({
      venueId: args.venueId,
      deviceId: args.deviceId,
      events: batch.map((event) => ({
        eventId: event.eventId,
        // NO streamType. It used to be hardcoded "Order" here, which was
        // correct for every event this till had ever sent and would have filed
        // the first `shift.opened` on the Order stream — a drawer that balances
        // on the device and does not exist on the server. The server derives it
        // from the registry, which is the only party that knows.
        //
        // Carried on the event. Guessing it from the payload made every event
        // without an orderId a stream of ONE — so a fold over "the order" would
        // have found a single event and nothing else.
        streamId: event.streamId,
        type: event.type,
        schemaVersion: 1,
        payload: event.payload,
        occurredAt: event.occurredAt.toISOString(),
        hlc: event.hlc,
        // OMITTED WHEN ABSENT, not sent as the string "undefined" — the same
        // failure the hlc had, and one that stores perfectly happily.
        ...(event.actorId ? { actorId: event.actorId } : {}),
      })),
    });

    const response = await send(`${args.baseUrl}/v1/events`, {
      method: "POST",
      headers: {
        authorization: `Bearer ${args.apiKey}`,
        "content-type": "application/json",
        "idempotency-key": idempotencyKey(batch, args.deviceId),
      },
      body,
    });

    if (!response.ok) {
      // THROWN, not returned. Outbox.drain() spares the attempt budget only for
      // a thrown transport failure; a returned rejection would burn it, and one
      // server restart would quarantine an evening of takings.
      throw new Error(`ingest answered ${response.status}`);
    }

    const result = (await response.json()) as {
      accepted?: string[];
      duplicates?: string[];
      rejected?: { eventId: string; reason: string }[];
    };

    return {
      accepted: result.accepted ?? [],
      // Acknowledged exactly like accepts. A client that treats a duplicate as
      // a failure retries forever and never clears its outbox.
      duplicates: result.duplicates ?? [],
      rejected: result.rejected ?? [],
    };
  };
}
